/**
 * Узел, в котором живут тосты (`ToastContainer`). Создаётся вне React: окно переносит его
 * в свой `<dialog>`, а узел, которым владеет React, переносить нельзя.
 *
 * Зачем переносить. Открытый через `showModal()` `<dialog>` в верхнем слое: всё вне него —
 * под затемнением и `inert`, `z-index` не помогает. Тост, показанный из окна, должен быть
 * виден и кликабелен, поэтому узел тостов лежит в верхнем открытом окне, а без окон — в `body`
 * (IMGM-1623, прототип — журнал плана, фаза 1).
 *
 * Стек открытых окон — свой, а не `modal-stack.ts`: тот учитывает только окна, которые
 * закрываются по Escape.
 */

import { theme } from 'styles/theme/theme';

const HOST_ID = 'toast_layer';

let host: HTMLDivElement | null = null;
const openDialogs: HTMLDialogElement[] = [];

export function getToastLayerHost(): HTMLDivElement {
  if (!host) {
    host = document.createElement('div');
    host.id = HOST_ID;
    // Вне потока: в `<dialog>` (флекс) узел не должен стать соседом окна и сдвинуть его.
    // Сам `ToastContainer` — `position: fixed` и меряется от экрана.
    host.style.position = 'fixed';
    host.style.top = '0';
    host.style.left = '0';
    host.style.width = '0';
    host.style.height = '0';
    // `position: fixed` всегда создаёт свой контекст наложения: `z-index: 9999` контейнера тостов
    // работает только внутри узла, а сам узел без своего слоя лежит на 0 — под шапкой (`sticky`),
    // и тост прятался за ней. В окне не важно: `<dialog>` и так в верхнем слое.
    host.style.zIndex = String(theme.zIndex.toast);
    document.body.appendChild(host);
  }
  return host;
}

function placeHost(): void {
  const target = openDialogs[openDialogs.length - 1] ?? document.body;
  const node = getToastLayerHost();
  if (node.parentNode !== target) target.appendChild(node);
}

/** Окно открылось — тосты переезжают в него. */
export function liftToastLayer(dialog: HTMLDialogElement): void {
  const index = openDialogs.indexOf(dialog);
  if (index !== -1) openDialogs.splice(index, 1);
  openDialogs.push(dialog);
  placeHost();
}

/**
 * Окно закрывается — тосты возвращаются в верхнее из оставшихся окон или в `body`.
 * Зовётся до того, как React уберёт `<dialog>` из DOM, иначе узел уйдёт вместе с ним.
 */
export function releaseToastLayer(dialog: HTMLDialogElement): void {
  const index = openDialogs.indexOf(dialog);
  if (index !== -1) openDialogs.splice(index, 1);
  placeHost();
}

/** Только для тестов: стек живёт в модуле и переживает размонтирование. */
export function resetToastLayer(): void {
  openDialogs.length = 0;
  if (host) document.body.appendChild(host);
}
